import { getSearchUrl, getSearchUrlWithFilters } from '@bridebook/toolbox';
import { addCountryToSearchUrl } from '@bridebook/toolbox/src/add-country-to-search-url';
import { CountryCodes, type Market } from '@bridebook/toolbox/src/gazetteer';
import type { Slug, UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { ILocationData } from '@bridebook/web/app-shared/lib/search/types';
import { IBreadcrumbItem } from '@bridebook/web/app-shared/lib/types';
import { prettySupplierCategory } from '@bridebook/web/app-shared/lib/utils';
import { getBreadcrumbsWithHome } from '@bridebook/web/app-shared/lib/utils/get-breadcrumbs-with-home';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { UrlHelper } from '@bridebook/web/lib/url-helper';

const getLocationBreadcrumbsData = ({
  locationData,
  slug,
  market,
  countryCode,
  isLoggedIn,
}: {
  locationData: ILocationData;
  slug: Slug | UrlFriendlySlug;
  market: Market;
  countryCode?: CountryCodes;
  isLoggedIn: boolean;
}): IBreadcrumbItem[] => {
  // Area suffix for UK is hardcoded to 'uk' as per SEO request in https://bridebook.atlassian.net/browse/LIVE-22684
  const isUK = market.country === CountryCodes.GB;
  const path = isUK
    ? getSearchUrlWithFilters({
        slug,
        filters: {},
        as: true,
        areaContext: [countryCode || market.country],
      })
    : getSearchUrl({ slug, as: true, fallbackToDefaultArea: false });

  const categoryName = prettySupplierCategory({
    slug,
    amount: 2,
    capitalize: true,
    addWedding: true,
    market,
    isLoggedIn,
  });
  const categoryItem = {
    name: categoryName,
    path,
    id: 'supplier-category',
  };

  let country = locationData?.country || undefined;
  if (country && country.toLowerCase() === 'uk') {
    country = 'United Kingdom';
  }

  const locationItems: { value?: string; id: string }[] = [
    { value: country, id: 'country' },
    { value: locationData?.county, id: 'county' },
    { value: locationData?.town, id: 'town' },
  ].filter(({ value }) => !!value);

  const parsedLocationItems: IBreadcrumbItem[] = locationItems.map((data) => {
    const name = data.value as string;
    const area = addCountryToSearchUrl(name, countryCode || market.country);

    return {
      id: data.id,
      name,
      path: getSearchUrl({ slug, area, as: true }),
    };
  });

  return [categoryItem, ...parsedLocationItems];
};

export const getBreadcrumbsLocation = ({
  locationData,
  slug = 'venues',
  name = '',
  isLoggedIn = false,
  market,
  countryCode,
}: {
  locationData: any;
  slug?: string;
  name?: string;
  isLoggedIn?: boolean;
  market: Market;
  countryCode?: CountryCodes;
}) => {
  const locationItems = getLocationBreadcrumbsData({
    countryCode,
    isLoggedIn,
    market,
    locationData,
    slug: slug as Slug,
  });

  const i18n = getI18n();

  const weddingSuppliersItem = {
    name: i18n.t('common:topBarLinks.suppliers'),
    path: UrlHelper.search,
    id: 'suppliers',
  } as IBreadcrumbItem;

  const showWeddingSuppliersItem = slug !== 'venue';

  const breadcrumbItems: IBreadcrumbItem[] = showWeddingSuppliersItem
    ? [weddingSuppliersItem, ...locationItems]
    : locationItems;

  if (!isLoggedIn) {
    return getBreadcrumbsWithHome(name, breadcrumbItems);
  }

  return [...locationItems, { name, id: 'name' }];
};
